import type { ModuleType, ShipClass } from "@station-keeping/sim";
import { PixelCanvas } from "../render/pixel";
import { moduleArt, shipArt } from "../render/sprites";

interface Thumb {
  url: string;
  width: number;
  height: number;
}

const cache = new Map<string, Thumb>();

/** Recadre un sprite sur ses pixels visibles (plus d'air autour du bloc). */
function crop(art: PixelCanvas): PixelCanvas {
  let left = art.width;
  let top = art.height;
  let right = -1;
  let bottom = -1;
  for (let y = 0; y < art.height; y++) {
    for (let x = 0; x < art.width; x++) {
      if (art.alphaAt(x, y) === 0) continue;
      left = Math.min(left, x);
      right = Math.max(right, x);
      top = Math.min(top, y);
      bottom = Math.max(bottom, y);
    }
  }
  if (right < 0) return art;
  const out = new PixelCanvas(right - left + 1, bottom - top + 1);
  out.stamp(art, -left, -top);
  return out;
}

function thumb(key: string, draw: () => PixelCanvas): Thumb {
  let found = cache.get(key);
  if (!found) {
    const art = crop(draw());
    found = { url: art.toCanvas().toDataURL("image/png"), width: art.width, height: art.height };
    cache.set(key, found);
  }
  return found;
}

export type ThumbSize = "sm" | "md" | "lg";

const UNITS: Record<ThumbSize, string> = { sm: "var(--art-sm)", md: "var(--art)", lg: "calc(var(--art) * 2)" };

/**
 * Vignette d'un sprite du plan (le vrai bâtiment, le vrai vaisseau), à un nombre entier de pixels d'écran
 * par pixel d'art : elle reste nette. Décorative : le nom est écrit à côté.
 */
function SpriteImage({ art, size, className }: { art: Thumb; size: ThumbSize; className?: string | undefined }) {
  const unit = UNITS[size];
  return (
    <img
      src={art.url}
      alt=""
      aria-hidden="true"
      draggable={false}
      className={`sprite ${className ?? ""}`}
      style={{ width: `calc(${unit} * ${art.width})`, height: `calc(${unit} * ${art.height})` }}
    />
  );
}

export function ModuleThumb({
  type,
  level = 1,
  size = "md",
  className,
}: {
  type: ModuleType;
  level?: number;
  size?: ThumbSize | "auto";
  className?: string;
}) {
  const art = thumb(`module-${type}-${level}`, () => moduleArt(type, level).body);
  // « auto » : les modules plats (dock, panneau) en grand, les hauts (noyau, habitat) à taille réelle.
  const resolved = size === "auto" ? (art.height <= 56 ? "lg" : "md") : size;
  return <SpriteImage art={art} size={resolved} className={className} />;
}

export function ShipThumb({ shipClass, size = "md", className }: { shipClass: ShipClass; size?: ThumbSize; className?: string }) {
  return <SpriteImage art={thumb(`ship-${shipClass}`, () => shipArt(shipClass).body)} size={size} className={className} />;
}
